import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Gauge } from './Gauge';
import { colourForDriverId } from '@/theme/tokens';
import type { AnnualStrategicDriver } from '@/types/models';
import { cn } from '@/lib/utils';
import { progressCounts } from '@/utils/drivers';

interface StrategicPulseBandProps {
  drivers: AnnualStrategicDriver[];
  /** The viewer's share of this week's time on driver-linked work; null
   *  when the week has no time in it yet. */
  alignmentPct?: number | null;
  className?: string;
}

interface DriverWithProgress extends AnnualStrategicDriver {
  /** Work done this financial year, 0–100; null when nothing is linked. */
  progress: number | null;
}

// Driver progress is the server's one figure (work done this financial
// year); null when nothing is linked this year, so the gauge shows a dash.
function progressOf(driver: AnnualStrategicDriver): number | null {
  const p = driver.progressPercentage;
  return p == null || !Number.isFinite(p) ? null : Math.max(0, Math.min(100, p));
}

// StrategicPulseBand — a full-width row of radial gauges, one per
// strategic driver, sorted high-to-low by progress this year. Each driver
// renders in its own colour from the deterministic palette so a
// driver always looks the same across dashboard, projects, calendar.
// Unpopulated drivers show a dash and a muted track.
export function StrategicPulseBand({
  drivers,
  alignmentPct,
  className,
}: StrategicPulseBandProps) {
  const enriched: DriverWithProgress[] = drivers
    .map(d => ({ ...d, progress: progressOf(d) }))
    .sort((a, b) => (b.progress ?? -1) - (a.progress ?? -1));

  return (
    <Card className={cn('w-full', className)}>
      <CardContent className="py-5">
        <div className="mb-4 flex items-baseline justify-between">
          <h3 className="text-base font-semibold">
            Strategic pulse{' '}
            <span className="text-xs font-normal text-muted-foreground">· work done this year</span>
          </h3>
          {typeof alignmentPct === 'number' && (
            <span className="text-xs text-muted-foreground">
              {alignmentPct}% of this week's time on your drivers
            </span>
          )}
        </div>
        <div
          className="grid gap-4 gap-y-5"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(116px, 1fr))' }}
        >
          {enriched.map(driver => {
            const colour = driver.progress === null
              ? 'hsl(var(--muted-foreground))'
              : colourForDriverId(driver.id);
            const label = driver.progress === null ? '–' : `${Math.round(driver.progress)}%`;
            const counts = progressCounts(driver);
            return (
              <div
                key={driver.id}
                className="flex flex-col items-center gap-2 text-center"
                title={counts ?? 'No linked work this year'}
              >
                <div className="relative h-[84px] w-[84px]">
                  <Gauge value={driver.progress ?? 0} colour={colour} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-base font-bold tracking-tight">{label}</span>
                  </div>
                </div>
                <div className="flex max-w-[130px] items-center justify-center gap-1.5 text-[11.5px] leading-tight text-muted-foreground">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: colour }}
                  />
                  <span className="line-clamp-2">{driver.title ?? 'Unnamed driver'}</span>
                </div>
              </div>
            );
          })}
        </div>
        {drivers.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No strategic drivers yet. Add a driver in Strategy to start the rhythm.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default StrategicPulseBand;
